import type { ExpandedState, OnChangeFn, SortingState } from "@tanstack/react-table"
import type { RefObject } from "react"
import { useCallback, useMemo, useState } from "react"
import { useShallow } from "zustand/react/shallow"

import { AP_AGING_DEFAULT_INVOICE_SORT_FIELD, AP_AGING_DEFAULT_INVOICE_SORT_ORDER } from "../constants"
import { useApAgingSearchStore } from "../stores/useApAgingSearchStore"
import { ApAgingInvoiceSortField, type ApAgingTotals, type ApAgingVendor } from "../types/apAgingReport"
import type { ApAgingReportSearchParams } from "../types/searchParams"
import { sortApAgingInvoices, toInvoiceSortingState } from "../utils"

import { SortingOrder } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"
import { toUpperSnakeCase } from "@/utils/utils"

interface UseApAgingReportListReturn {
  vendors: ApAgingVendor[]
  summary: ApAgingTotals
  isFetching: boolean
  error: Error | undefined
  searchParams: ApAgingReportSearchParams
  invoiceSorting: SortingState
  expanded: ExpandedState
  tableContainerRef: RefObject<HTMLDivElement>
  handleScroll: () => void
  handleExpandedChange: OnChangeFn<ExpandedState>
  handleInvoiceSortingChange: OnChangeFn<SortingState>
  handleSearchChange: (query: string) => void
  handleFilterChange: <K extends keyof ApAgingReportSearchParams>(key: K, value: ApAgingReportSearchParams[K]) => void
  handleFiltersChange: (updates: Partial<ApAgingReportSearchParams>) => void
  handleFilterRemove: (
    keyOrKeys: keyof ApAgingReportSearchParams | (keyof ApAgingReportSearchParams)[],
    value?: string | number
  ) => void
  handleFilterClear: () => void
}

const ROUTE = "/app/financial-reporting/_layout/ap-aging-report"

const FILTER_KEYS: (keyof ApAgingReportSearchParams)[] = [
  "query",
  "statuses",
  "agingBuckets",
  "dueDateFrom",
  "dueDateTo",
]

// An empty status selection is meaningful: it is kept in the URL instead of falling back to the default.
const PRESERVED_EMPTY_FILTER_KEYS: (keyof ApAgingReportSearchParams)[] = ["statuses"]

const EMPTY_SUMMARY: ApAgingTotals = { current: 0, days1To30: 0, days31To60: 0, days61To90: 0, over90: 0, total: 0 }

const DEFAULT_INVOICE_SORT = {
  field: AP_AGING_DEFAULT_INVOICE_SORT_FIELD,
  order: AP_AGING_DEFAULT_INVOICE_SORT_ORDER,
}

export const useApAgingReportList = (): UseApAgingReportListReturn => {
  const store = useApAgingSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )
  const summary = useApAgingSearchStore((state) => state.meta) ?? EMPTY_SUMMARY

  const {
    data: loadedVendors,
    isFetching,
    error,
    searchParams,
    expanded,
    tableContainerRef,
    handleScroll,
    handleExpandedChange,
    handleSearchChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear: clearFilters,
  } = useListPageBase<ApAgingVendor, ApAgingReportSearchParams>({
    route: ROUTE,
    store,
    filterKeys: FILTER_KEYS,
    preservedEmptyFilterKeys: PRESERVED_EMPTY_FILTER_KEYS,
  })

  // AK-6964 AC7: The invoices of a vendor are sorted on the screen and the sort is not part of the URL: it only reorders
  // the rows already loaded, so it must neither reload the report nor close the open vendors.
  const [invoiceSort, setInvoiceSort] = useState(DEFAULT_INVOICE_SORT)

  const invoiceSorting = useMemo(
    () => toInvoiceSortingState(invoiceSort.field, invoiceSort.order),
    [invoiceSort.field, invoiceSort.order]
  )

  const vendors = useMemo(
    () =>
      loadedVendors.map((vendor) => ({
        ...vendor,
        invoices: sortApAgingInvoices(vendor.invoices, invoiceSort.field, invoiceSort.order),
      })),
    [loadedVendors, invoiceSort.field, invoiceSort.order]
  )

  const handleInvoiceSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const [sort] = typeof updater === "function" ? updater(invoiceSorting) : updater
      const field = Object.values(ApAgingInvoiceSortField).find(
        (sortField) => sortField === toUpperSnakeCase(sort?.id ?? "")
      )
      if (!sort || !field) return

      setInvoiceSort({ field, order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc })
    },
    [invoiceSorting]
  )

  // AK-6964 AC9: Clear All resets the report to its default state, including the invoice sort.
  const handleFilterClear = useCallback(() => {
    clearFilters()
    setInvoiceSort(DEFAULT_INVOICE_SORT)
  }, [clearFilters])

  return {
    vendors,
    summary,
    isFetching,
    error,
    searchParams,
    invoiceSorting,
    expanded,
    tableContainerRef,
    handleScroll,
    handleExpandedChange,
    handleInvoiceSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFiltersChange,
    handleFilterRemove,
    handleFilterClear,
  }
}
